import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Card,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  Add,
  Refresh,
  ContactSupport,
  Web,
  Smartphone,
  Edit,
} from '@mui/icons-material';
import DataTable, { Column } from '../components/common/DataTable';
import WhatsAppLink from '../components/common/WhatsAppLink';
import { useAppDispatch } from '../hooks/redux';
import { addNotification } from '../store/slices/uiSlice';
import { format } from 'date-fns';
import {
  inquiryService,
  Inquiry,
  InquiryStatus,
  InquirySource,
} from '../services/inquiryService';

const statusConfig: Record<string, { color: any; label: string }> = {
  NEW: { color: 'info', label: 'New' },
  IN_PROGRESS: { color: 'warning', label: 'In Progress' },
  CONTACTED: { color: 'primary', label: 'Contacted' },
  COMPLETED: { color: 'success', label: 'Completed' },
};

const sourceConfig: Record<string, { icon: React.ReactNode; label: string }> = {
  WEBSITE: { icon: <Web fontSize="small" />, label: 'Website' },
  AI_ASSISTANT: { icon: <Smartphone fontSize="small" />, label: 'AI App' },
};

const columns: Column[] = [
  {
    id: 'name',
    label: 'Name',
    format: (value: string, row: Inquiry) => (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
        {row.phone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {row.phone}
            </Typography>
            <WhatsAppLink
              phone={row.phone}
              message={`Hi ${value}, thanks for reaching out${row.serviceOfInterest ? ` about ${row.serviceOfInterest}` : ''}.`}
            />
          </Box>
        )}
      </Box>
    ),
  },
  {
    id: 'email',
    label: 'Email',
    format: (value: string) => (
      <Typography variant="body2">{value || '-'}</Typography>
    ),
  },
  {
    id: 'serviceOfInterest',
    label: 'Service',
    format: (value: string) => (
      <Typography variant="body2" sx={{ maxWidth: 150 }} noWrap>
        {value || '-'}
      </Typography>
    ),
  },
  {
    id: 'source',
    label: 'Source',
    format: (value: string) => {
      const cfg = sourceConfig[value] || sourceConfig.WEBSITE;
      return (
        <Chip
          icon={cfg.icon as any}
          label={cfg.label}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      );
    },
  },
  {
    id: 'status',
    label: 'Status',
    format: (value: string) => {
      const cfg = statusConfig[value] || statusConfig.NEW;
      return (
        <Chip
          label={cfg.label}
          size="small"
          color={cfg.color}
          sx={{ fontWeight: 600 }}
        />
      );
    },
  },
  {
    id: 'createdAt',
    label: 'Received',
    format: (value: string) => (
      <Typography variant="body2">
        {format(new Date(value), 'MMM dd, yyyy HH:mm')}
      </Typography>
    ),
  },
];

const Inquiries: React.FC = () => {
  const dispatch = useAppDispatch();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [fetching, setFetching] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<InquiryStatus>('NEW');
  const [updateRemarks, setUpdateRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchInquiries = async () => {
    setFetching(true);
    try {
      const result = await inquiryService.getInquiries({
        page: 1,
        limit: 100,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        source: sourceFilter !== 'all' ? sourceFilter : undefined,
      });
      setInquiries(result.inquiries || []);
    } catch (err) {
      console.error('Failed to fetch inquiries:', err);
      dispatch(
        addNotification({
          type: 'error',
          message:
            err instanceof Error ? err.message : 'Failed to load inquiries',
        })
      );
      setInquiries([]);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, [searchTerm, statusFilter, sourceFilter]);

  const handleView = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setDetailOpen(true);
  };

  const handleUpdateClick = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setUpdateStatus((inquiry.status as InquiryStatus) || 'NEW');
    setUpdateRemarks(inquiry.remarks || '');
    setUpdateOpen(true);
  };

  const handleUpdateSubmit = async () => {
    if (!selectedInquiry) return;
    setSubmitting(true);
    try {
      const updated = await inquiryService.updateInquiry(selectedInquiry.id, {
        status: updateStatus,
        remarks: updateRemarks,
      });
      setInquiries((prev) =>
        prev.map((i) => (i.id === selectedInquiry.id ? updated : i))
      );
      setSelectedInquiry(updated);
      setUpdateOpen(false);
      dispatch(
        addNotification({
          type: 'success',
          message: 'Inquiry status updated successfully',
        })
      );
    } catch (err) {
      dispatch(
        addNotification({
          type: 'error',
          message:
            err instanceof Error ? err.message : 'Failed to update inquiry',
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (inquiry: Inquiry) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?'))
      return;
    try {
      await inquiryService.deleteInquiry(inquiry.id);
      setInquiries((prev) => prev.filter((i) => i.id !== inquiry.id));
      setDetailOpen(false);
      setUpdateOpen(false);
      setSelectedInquiry(null);
      dispatch(
        addNotification({
          type: 'success',
          message: 'Inquiry deleted successfully',
        })
      );
    } catch (err) {
      dispatch(
        addNotification({
          type: 'error',
          message:
            err instanceof Error ? err.message : 'Failed to delete inquiry',
        })
      );
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      const created = await inquiryService.createInquiry({
        name: data.name,
        phone: data.phone,
        email: data.email,
        serviceOfInterest: data.serviceOfInterest,
        message: data.message,
        source: (data.source as InquirySource) || 'WEBSITE',
      });
      setInquiries((prev) => [created, ...prev]);
      setFormOpen(false);
      dispatch(
        addNotification({
          type: 'success',
          message: 'Inquiry created successfully',
        })
      );
    } catch (err) {
      dispatch(
        addNotification({
          type: 'error',
          message:
            err instanceof Error ? err.message : 'Failed to create inquiry',
        })
      );
    }
  };

  const paginatedInquiries = inquiries.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ContactSupport sx={{ fontSize: 40, color: '#E8B4B8' }} />
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Inquiries
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage inquiries from website & AI Assistant app
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchInquiries}
                disabled={fetching}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setFormOpen(true)}
                sx={{
                  bgcolor: '#E8B4B8',
                  '&:hover': { bgcolor: '#d9a0a4' },
                }}
              >
                Add Inquiry
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name, email, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {Object.entries(statusConfig).map(([k, v]) => (
                      <MenuItem key={k} value={k}>
                        {v.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Source</InputLabel>
                  <Select
                    value={sourceFilter}
                    label="Source"
                    onChange={(e) => setSourceFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="WEBSITE">Website</MenuItem>
                    <MenuItem value="AI_ASSISTANT">AI App</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <DataTable
            columns={columns}
            rows={paginatedInquiries}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={inquiries.length}
            onPageChange={setPage}
            onRowsPerPageChange={(l) => {
              setRowsPerPage(l);
              setPage(0);
            }}
            onView={handleView}
            onEdit={handleUpdateClick}
            onDelete={handleDelete}
            loading={fetching}
          />
        </Grid>
      </Grid>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Inquiry Details</DialogTitle>
        <DialogContent>
          {selectedInquiry && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Name
              </Typography>
              <Typography variant="body1" gutterBottom>
                {selectedInquiry.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Contact
              </Typography>
              <Typography
                variant="body1"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {selectedInquiry.phone || '-'} {selectedInquiry.email && `• ${selectedInquiry.email}`}
                {selectedInquiry.phone && (
                  <WhatsAppLink
                    phone={selectedInquiry.phone}
                    message={`Hi ${selectedInquiry.name}, thanks for reaching out${selectedInquiry.serviceOfInterest ? ` about ${selectedInquiry.serviceOfInterest}` : ''}.`}
                  />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Service of Interest
              </Typography>
              <Typography variant="body1" gutterBottom>
                {selectedInquiry.serviceOfInterest || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Message
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedInquiry.message || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Source
              </Typography>
              <Typography variant="body1" gutterBottom>
                {(sourceConfig[selectedInquiry.source] || sourceConfig.WEBSITE).label}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={(statusConfig[selectedInquiry.status] || statusConfig.NEW).label}
                  color={(statusConfig[selectedInquiry.status] || statusConfig.NEW).color}
                  size="small"
                />
              </Box>
              {selectedInquiry.remarks && (
                <>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Remarks
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedInquiry.remarks}
                  </Typography>
                </>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                Received: {format(new Date(selectedInquiry.createdAt), 'PPpp')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
          {selectedInquiry && (
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => {
                setDetailOpen(false);
                handleUpdateClick(selectedInquiry);
              }}
              sx={{ bgcolor: '#E8B4B8', '&:hover': { bgcolor: '#d9a0a4' } }}
            >
              Update Status
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={updateOpen} onClose={() => setUpdateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Status & Remarks</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={updateStatus}
                label="Status"
                onChange={(e) => setUpdateStatus(e.target.value as InquiryStatus)}
              >
                {Object.entries(statusConfig).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Remarks"
              placeholder="Add notes for this inquiry..."
              value={updateRemarks}
              onChange={(e) => setUpdateRemarks(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateSubmit}
            disabled={submitting}
            sx={{ bgcolor: '#E8B4B8', '&:hover': { bgcolor: '#d9a0a4' } }}
          >
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Inquiry Form Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Inquiry</DialogTitle>
        <DialogContent>
          <InquiryForm
            onSubmit={handleFormSubmit}
            onCancel={() => setFormOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const InquiryForm: React.FC<{
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ onSubmit, onCancel }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [serviceOfInterest, setServiceOfInterest] = useState('');
  const [message, setMessage] = useState('');
  const [source, setSource] = useState<InquirySource>('WEBSITE');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, phone, email, serviceOfInterest, message, source });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField
          fullWidth
          required
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextField
          fullWidth
          label="Service of Interest"
          value={serviceOfInterest}
          onChange={(e) => setServiceOfInterest(e.target.value)}
        />
        <FormControl fullWidth>
          <InputLabel>Source</InputLabel>
          <Select
            value={source}
            label="Source"
            onChange={(e) => setSource(e.target.value as InquirySource)}
          >
            <MenuItem value="WEBSITE">Website</MenuItem>
            <MenuItem value="AI_ASSISTANT">AI Assistant App</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="contained" sx={{ bgcolor: '#E8B4B8', '&:hover': { bgcolor: '#d9a0a4' } }}>
            Create
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default Inquiries;
